import React from 'react';
import { HiArrowNarrowRight } from 'react-icons/hi';
import { Link } from 'react-scroll';

import profilePhotoLight from '../assets/profile.png';
import profilePhotoDark from '../assets/profile-dark.png';
import SocialLinks from './SocialLinks';
import { PORTFOLIO_WORK_FILTER_EVENT } from './Work';

const About = () => {
  return (
    <div
      name='about'
      className='w-full bg-white text-gray-900 dark:bg-[#0a192f] dark:text-gray-300'
    >
      {/* Hero — full viewport width & height below fixed nav; tighter padding on mobile */}
      <div
        className='flex flex-col items-center w-full px-6 sm:px-10 md:px-14 lg:px-20 xl:px-28 2xl:px-32
        pt-4 pb-8 min-h-0 justify-start
        md:min-h-[calc(100dvh-80px)] md:justify-center md:py-16'
      >
        <div className='w-full max-w-[1600px] mx-auto text-center flex flex-col items-center'>
          <p className='text-pink-600 text-base sm:text-lg'>Hi, I'm</p>
          <h1 className='mt-2 font-bold text-gray-900 dark:text-[#ccd6f6] tracking-tight leading-[1.05] whitespace-nowrap text-[clamp(2.25rem,11vw,7.5rem)]'>
            Johnedel Mapa
          </h1>
          <h2 className='mt-3 sm:mt-4 font-bold text-gray-800 dark:text-[#8892b0] leading-tight whitespace-nowrap text-[clamp(0.7rem,3.4vw,3rem)] max-w-full px-1'>
            <span className='text-gray-800 dark:text-[#8892b0]'>Full-Stack Developer</span>{' '}
            <span className='text-pink-600 dark:text-pink-400'>· Automation Consultant</span>
          </h2>
          <p className='text-gray-600 dark:text-[#8892b0] py-4 sm:py-6 md:py-8 max-w-4xl text-lg sm:text-xl md:text-2xl leading-relaxed'>
            I help businesses automate workflows and build reliable web systems—turning complex processes into simple,
            measurable results.
          </p>
          <p className='text-base sm:text-lg text-gray-500 dark:text-gray-500 max-w-4xl pb-4 md:pb-6'>
            <span className='font-semibold text-gray-700 dark:text-gray-400'>Johnedel Mapa</span> — consulting and
            implementation for teams that want operations to run without admin fatigue.
          </p>
          <div className='flex flex-col sm:flex-row flex-wrap justify-center items-stretch gap-3 sm:gap-4 w-full max-w-md mx-auto sm:max-w-none'>
            <Link
              className='w-full sm:w-auto'
              to='case-studies'
              smooth={true}
              duration={500}
              offset={-80}
              onClick={() =>
                window.dispatchEvent(new CustomEvent(PORTFOLIO_WORK_FILTER_EVENT, { detail: 'Systems' }))
              }
            >
              <button
                type='button'
                className='group border-2 px-8 py-3.5 sm:py-4 text-base sm:text-lg w-full inline-flex items-center justify-center gap-2
              text-gray-900 border-gray-900 hover:bg-pink-600 hover:border-pink-600 hover:text-white
              dark:text-white dark:border-gray-300 dark:hover:border-pink-600'
              >
                <span>Systems built</span>
                <span className='group-hover:rotate-90 duration-300 shrink-0'>
                  <HiArrowNarrowRight className='text-xl' />
                </span>
              </button>
            </Link>
            <Link
              className='w-full sm:w-auto'
              to='case-studies'
              smooth={true}
              duration={500}
              offset={-80}
              onClick={() =>
                window.dispatchEvent(new CustomEvent(PORTFOLIO_WORK_FILTER_EVENT, { detail: 'Automation' }))
              }
            >
              <button
                type='button'
                className='group border-2 px-8 py-3.5 sm:py-4 text-base sm:text-lg w-full inline-flex items-center justify-center gap-2
              border-pink-600 text-pink-600 hover:bg-pink-600 hover:text-white
              dark:border-pink-400 dark:text-pink-400 dark:hover:bg-pink-600 dark:hover:text-white'
              >
                <span>Automation projects</span>
                <span className='group-hover:rotate-90 duration-300 shrink-0'>
                  <HiArrowNarrowRight className='text-xl' />
                </span>
              </button>
            </Link>
          </div>
        </div>
      </div>

      {/* About — person + brand + social (professional, no “social-only” hover photo) */}
      <div className='flex flex-col justify-center items-center w-full border-t border-gray-200 dark:border-gray-800 pt-12 md:pt-16 lg:pt-20'>
        <div className='max-w-[1000px] w-full grid grid-cols-2 gap-8 px-4'>
          <div className='sm:text-right pb-4 pl-2 sm:pl-4'>
            <p className='text-5xl font-bold inline border-b-4 border-pink-600'>About</p>
          </div>
          <div />
        </div>

        <div className='max-w-[1000px] w-full px-4 pb-10'>
          <div className='grid gap-10 md:grid-cols-[minmax(0,220px)_1fr] items-start'>
            <div className='mx-auto md:mx-0 w-[200px] shrink-0 sm:w-[220px]'>
              <div className='overflow-hidden rounded-2xl border border-gray-200 bg-gray-100 shadow-lg ring-1 ring-black/5 dark:border-transparent dark:bg-transparent dark:shadow-none dark:ring-1 dark:ring-white/10'>
                <img
                  src={profilePhotoLight}
                  alt='Johnedel Mapa'
                  className='block w-full h-auto object-cover object-top dark:hidden'
                  width={400}
                  height={500}
                  loading='lazy'
                />
                <img
                  src={profilePhotoDark}
                  alt='Johnedel Mapa'
                  className='hidden w-full h-auto object-cover object-top dark:block'
                  width={400}
                  height={500}
                  loading='lazy'
                />
              </div>
              <p className='mt-3 text-center md:text-left text-xs text-gray-500 dark:text-gray-500'>
                Full-stack delivery · Automation specialist
              </p>
            </div>
            <div>
              <p className='text-2xl sm:text-3xl font-bold text-gray-900 dark:text-[#ccd6f6] mb-4'>
                The person behind the work
              </p>
              <p className='leading-relaxed text-gray-700 dark:text-gray-300'>
                I'm Johnedel—I partner with teams to turn ideas into software and automation that hold up in production.
                I work like a technical advisor: prioritize what to build or automate first, align with how your business
                runs, and choose tools that earn their place—whether that is Ruby on Rails, Next.js, Vue, WordPress, or
                n8n workflows that connect your stack on schedule.
              </p>
              <p className='leading-relaxed mt-4 text-gray-700 dark:text-gray-300'>
                From startups to larger organizations, fewer manual steps, smoother handoffs, and experiences your clients
                trust—so technology supports your goals, not the other way around.
              </p>
              <div className='mt-8'>
                <p className='text-sm font-semibold uppercase tracking-wider text-pink-600 mb-3'>Connect</p>
                <SocialLinks />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
